import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/layout/Navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, Mail } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const lastUpdated = "8 July 2026";
  
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>
        
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#5DBDB3]/10 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#5DBDB3]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>
        </div>
        
        <Card>
          <CardContent className="prose prose-slate max-w-none p-8">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Seen Beyond the Label (&quot;we&quot;, &quot;our&quot;, &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our website and 
                mobile application (collectively, the &quot;Service&quot;).
              </p>
              <p className="text-muted-foreground">
                We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. 
                By using our Service, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mb-2">2.1 Personal Information</h3>
              <p className="text-muted-foreground mb-4">When you register for an account, we collect:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4">
                <li>Name and email address</li>
                <li>Password (encrypted)</li>
                <li>Allergy and dietary information you choose to provide</li>
                <li>Payment information (processed securely by Stripe)</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2">2.2 Usage Data</h3>
              <p className="text-muted-foreground mb-4">We automatically collect:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4">
                <li>Products you scan (barcodes, images)</li>
                <li>Analysis results and history</li>
                <li>Device information and IP address</li>
                <li>Usage patterns and preferences</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2">2.3 Hardship Access Applications</h3>
              <p className="text-muted-foreground">
                If you apply for Hardship Access, we collect proof of benefits documentation. This sensitive 
                information is stored securely and used solely to verify eligibility. Documents are deleted 
                within 30 days of verification.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Provide and maintain our Service</li>
                <li>Analyse ingredients and identify potential allergens</li>
                <li>Personalise your experience based on your allergy profile</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send important service updates and notifications</li>
                <li>Improve our AI analysis accuracy</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Legal Basis for Processing (UK GDPR)</h2>
              <p className="text-muted-foreground mb-4">We process your data under the following legal bases:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li><strong>Contract:</strong> To provide you with our Service</li>
                <li><strong>Legitimate Interests:</strong> To improve our Service and prevent fraud</li>
                <li><strong>Consent:</strong> For marketing communications (which you can withdraw at any time)</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li><strong>Service Providers:</strong> Stripe (payments), MongoDB (database hosting), AI providers for analysis</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with any merger or acquisition</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                We do NOT sell your personal information to third parties.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Your Rights (UK GDPR)</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate personal data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data (&quot;right to be forgotten&quot;)</li>
                <li><strong>Restriction:</strong> Restrict processing of your personal data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, contact us at privacy@seenbeyondthelabel.co.uk
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your personal data for as long as your account is active or as needed to provide services. 
                Scan history is retained for 2 years. You can request deletion at any time. Some data may be retained 
                longer for legal compliance or legitimate business purposes.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Data Security</h2>
              <p className="text-muted-foreground">
                We implement appropriate technical and organisational measures to protect your personal data, including 
                encryption, secure servers, and access controls. However, no method of transmission over the Internet 
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. International Transfers</h2>
              <p className="text-muted-foreground">
                Your data may be transferred to and processed in countries outside the UK. We ensure appropriate 
                safeguards are in place, such as Standard Contractual Clauses, to protect your data in accordance 
                with UK GDPR requirements.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. Children&apos;s Privacy</h2>
              <p className="text-muted-foreground">
                Our Service is not intended for children under 16. We do not knowingly collect personal information 
                from children under 16. If you become aware that a child has provided us with personal data, please 
                contact us.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. Cookies</h2>
              <p className="text-muted-foreground">
                We use essential cookies for authentication and session management. We do not use tracking cookies 
                or third-party advertising cookies. You can control cookies through your browser settings.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new policy on this page and updating the &quot;Last updated&quot; date.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                If you have questions about this Privacy Policy or wish to exercise your rights, contact us:
              </p>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm"><strong>Data Controller:</strong> Seen Beyond the Label Ltd</p>
                <p className="text-sm"><strong>Email:</strong> privacy@seenbeyondthelabel.co.uk</p>
                <p className="text-sm"><strong>Address:</strong> [Your registered address]</p>
                <p className="text-sm mt-2">
                  <strong>Supervisory Authority:</strong> You have the right to lodge a complaint with the 
                  Information Commissioner&apos;s Office (ICO) at ico.org.uk
                </p>
              </div>
            </section>
          </CardContent>
        </Card>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border mt-12 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Seen Beyond the Label Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
